import { useMemo, useState } from 'react';
import { Select, SelectItem } from '@carbon/react';
import { keyBy, mapValues } from 'lodash';

import BusinessUnitBreakdownChart from 'features/dashboard/business-unit/BusinessUnitBreakdownChart';
import BusinessUnitBreakdownTable from 'features/dashboard/business-unit/BusinessUnitBreakdownTable';
import SectionHeader from 'features/dashboard/business-unit/components/SectionHeader';
import useBusinessUnitsQuery from 'shared/api/business-units/useBusinessUnitsQuery';
import useSpecialtySpacesQuery from 'shared/api/specialty-spaces/useSpecialtySpacesQuery';
import QuickViewBox from 'shared/components/quick-view/QuickViewBox';
import { formatNumeric } from 'utils/format';
import styles from 'theme/features/dashboard/dashboard-business-unit.module.scss';

const BusinessUnitProgramBreakdown = ({ breakdown, measurementSystem, isCompact }) => {
  const { data: businessUnits } = useBusinessUnitsQuery();
  const { data: specialtySpaces } = useSpecialtySpacesQuery();

  const breakdownsWithHeadcount = breakdown?.filter((b) => b.headcount || b.total.area) ?? [];
  const businessUnitsWithBreakdown = breakdownsWithHeadcount.map((b) => b.id);

  const businessUnitNameById = mapValues(keyBy(businessUnits, 'id'), (value) => value.name);

  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);

  const selectedBusinessUnitBreakdown = useMemo(
    () => breakdown?.find((b) => b.id === selectedBusinessUnitId),
    [breakdown, selectedBusinessUnitId]
  );

  if (!specialtySpaces) {
    return null;
  }

  const renderOverview = (name, value, tooltip) => (
    <QuickViewBox key={name} name={name} value={value} tooltip={tooltip} />
  );

  const renderBreakdown = (businessUnitBreakdown) => (
    <div key={businessUnitBreakdown.id} className={styles['breakdown-item']}>
      <SectionHeader businessUnitName={businessUnitNameById[businessUnitBreakdown.id]} />
      <div className={styles['breakdown-wrapper__overview']}>
        {renderOverview(
          'Headcount',
          formatNumeric(businessUnitBreakdown.headcount),
          'Number of seated and mobile population. It does not include people who work from home full-time or work at client sites.'
        )}
        {renderOverview(
          'Attending Population',
          formatNumeric(businessUnitBreakdown.attendance),
          'The average amount of time Business Unit expect their population to be on site.'
        )}
        {renderOverview(
          'Attendance Rate',
          `${formatNumeric(businessUnitBreakdown.attendanceRate)}%`,
          'Total number of on-site population per the Attendance Rate provided or specified as default in the Inputs page.'
        )}
      </div>
      <BusinessUnitBreakdownTable
        breakdown={businessUnitBreakdown}
        specialtySpaces={specialtySpaces}
        measurementSystem={measurementSystem}
      />
    </div>
  );

  const renderBreakdownChart = (businessUnitBreakdown) => (
    <div key={businessUnitBreakdown.id} className={styles['breakdown-item']}>
      <SectionHeader withSubtitle />
      <BusinessUnitBreakdownChart
        breakdown={businessUnitBreakdown}
        measurementSystem={measurementSystem}
        isCompact={isCompact}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <Select
        id="business-unit"
        labelText="Business Unit"
        onChange={(e) => setSelectedBusinessUnitId(e.target.value === '' ? null : Number(e.target.value))}
        className={styles.select}
      >
        <SelectItem value="" text="All Business Units" />
        {businessUnits?.map((x) => (
          <SelectItem key={x.id} value={x.id} disabled={!businessUnitsWithBreakdown.includes(x.id)} text={x.name} />
        ))}
      </Select>
      <div className={styles.content}>
        <div className={styles['breakdown-wrapper']}>
          {selectedBusinessUnitBreakdown
            ? renderBreakdown(selectedBusinessUnitBreakdown)
            : breakdownsWithHeadcount.map(renderBreakdown)}
        </div>
        <div className={styles['chart-wrapper']}>
          {selectedBusinessUnitBreakdown
            ? renderBreakdownChart(selectedBusinessUnitBreakdown)
            : breakdownsWithHeadcount.map(renderBreakdownChart)}
        </div>
      </div>
    </div>
  );
};

export default BusinessUnitProgramBreakdown;
